import {Formik} from "formik";
import {useEffect, useRef, useState} from "react";
import * as Yup from "yup";
import {useGetCatgeory} from "../../../hooks/useCategory";
import {useAddOffer, useOffer} from "../../../hooks/useOffers";
import {useDeleteFile} from "../../../hooks/useFile";
import Loader from "../../components/shared/Loader";
import ImageComponent from "../../components/shared/ImageComponent";
import UpImage from "../../../images/image-upload.svg";
import NoImage from "../../../images/NoImage.jpg";
import {buildImageUrl} from "../../../utils/imageUrlBuilder";
import CustomSelectDropdown from "./selectDropDown";
import "./customEditor.css";
import {Link, useNavigate} from "react-router-dom";
import {geocodeByPlaceId} from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "../../components/shared/SafeGooglePlacesAutocomplete";
import {generatePrettyUrl} from "../../../utils/prettyUrl";
import {
  emailRegex,
  fileFormat,
  offerStatus,
  phoneRegex,
} from "../../../utils/constants";
import {showToastModal} from "../../components/shared/ToastModalSwal";
import videoImage from "../../../images/thumbVideo.png";
import {
  MAX_OFFER_ATTACHMENTS,
  mergeOfferAttachments,
} from "../../../utils/offerAttachments";
import googleMapsApiKey from "../../../config/googleMaps";
import QueryErrorState from "../../components/shared/QueryErrorState";
import UploadPreviewState from "../../components/shared/UploadPreviewState";

const PageUpdateAnnonce = () => {
  const [selectedCat, setSelectedCat] = useState(null);
  const [subCatIds, setSubCatIds] = useState([]);
  const [subCatIdsError, setSubCatIdsError] = useState(false);
  const [subCatValue, setSubCatValue] = useState("");
  const [images, setImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [files, setFiles] = useState([]);
  const [deleteFiles, setDeleteFiles] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef();
  const {onUpdateOfferAsync} = useAddOffer();
  const {onDelete} = useDeleteFile();
  const {data: offer, isLoading, isError, error, refetch} = useOffer();
  const {data: categories} = useGetCatgeory();

  useEffect(() => {
    if (offer?.SubCategories?.length && categories?.length) {
      setSubCatIds(
        offer.SubCategories.map((el) => ({id: el.id, title: el.title})),
      );
      const categoryObject = categories.find(
        (ele) => ele.id === offer.SubCategories[0].Category.id,
      );
      if (categoryObject) setSelectedCat(categoryObject);
    }
    if (offer?.FileUploads?.length) {
      setFiles([...offer.FileUploads]);
    }
  }, [offer, categories]);

  if (isLoading) return <Loader />;
  if (isError) return <QueryErrorState error={error} onRetry={refetch} />;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addAttachments = (fileList) => {
    const {attachments, errors} = mergeOfferAttachments({
      currentAttachments: images,
      incomingFiles: fileList,
      existingCount: files.length,
    });
    setImages(attachments);

    if (errors.length) {
      showToastModal({
        success: false,
        title: "Erreur d'importation",
        message: errors.join(" "),
        buttonTitle: "Fermer",
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addAttachments(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDiplome = (e) => {
    const input = e.target;
    addAttachments(input.files);
    input.value = "";
  };

  const handleDeleteFile = (id) => {
    setDeleteFiles((prev) => [...prev, id]);
    setFiles((prev) => prev.filter((el) => el.id !== id));
  };

  const handleDelete = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index]?.imageUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSelect = (e) => {
    const categoryObject = e;
    if (categoryObject) setSelectedCat(categoryObject);
    setSubCatValue("");
  };
  const handlePlaceSelect = async (address, setFieldValue) => {
    const [place] = await geocodeByPlaceId(address.value.place_id);

    const {long_name: postalCode = ""} =
      place.address_components.find((c) => c.types.includes("postal_code")) ||
      {};
    const {long_name: ville = ""} =
      place.address_components.find((c) => c.types.includes("locality")) || {};

    const street =
      place.address_components.find((c) => c.types.includes("route"))
        ?.long_name || "";
    const streetNumber =
      place.address_components.find((c) => c.types.includes("street_number"))
        ?.long_name || "";

    setFieldValue("streetNumber", streetNumber);
    setFieldValue("street", street);

    setFieldValue("address", address.label);
    setFieldValue("postCode", postalCode);
    setFieldValue("ville", ville);
    setSelectedAddress(address);
  };

  const handleSelectSub = (e) => {
    const subCategory = e;
    setSubCatValue(e);

    if (subCategory) {
      setSubCatIds((prev) => {
        // Check if the subCategory ID already exists in the array
        const exists = prev.some((item) => item.id === subCategory.id);

        if (!exists) {
          // Add the new subCategory if it doesn't already exist
          return [...prev, {id: subCategory.id, title: subCategory.title}];
        }

        // Return the existing array if no changes are needed
        return prev;
      });
    }
  };

  const formSchema = Yup.object().shape({
    title: Yup.string()
      .required("Le titre est requis")
      .min(2, "Le titre doit contenir au moins 2 caractères")
      .test(
        "no-phone-or-email",
        "Le titre ne doit pas contenir de numéro de téléphone ou d'adresse e-mail",
        (value) => {
          if (!value) return true;
          return !emailRegex.test(value) && !phoneRegex.test(value);
        },
      ),
    description: Yup.string()
      .required("La description est requise")
      .min(2, "La description doit contenir au moins 2 caractères")
      .test(
        "no-phone-or-email",
        "La description ne doit pas contenir de numéro de téléphone ou d'adresse e-mail",
        (value) => {
          if (!value) return true;
          return !emailRegex.test(value) && !phoneRegex.test(value);
        },
      ),
    address: Yup.string()
      .required("L'adresse est requise")
      .min(5, "L'adresse doit contenir au moins 5 caractères"),
    ville: Yup.string()
      .required("La ville est requise")
      .min(2, "La ville doit contenir au moins 2 caractères"),
    street: Yup.string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema.required(
            "Veuillez entrer votre adresse complète contenant le nom de rue",
          ),

        otherwise: (schema) => schema.notRequired(),
      }),
    streetNumber: Yup.string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema.required(
            "Veuillez entrer votre adresse complète contenant le numéro de la rue",
          ),

        otherwise: (schema) => schema.notRequired(),
      }),

    postCode: Yup.string()
      .required("Le code postal est requis")
      .matches(/^\d{5}$/, "Le code postal doit contenir exactement 5 chiffres"),
    minBudget: Yup.number()
      .required("Le budget minimum est requis")
      .positive("Le budget minimum doit être un nombre positif")
      .min(1, "Le budget minimum doit être supérieur ou égal à 1 euro"),
    maxBudget: Yup.number()
      .required("Le budget maximum est requis")
      .positive("Le budget maximum doit être un nombre positif")
      .moreThan(
        Yup.ref("minBudget"),
        "Le budget maximum doit être supérieur au budget minimum",
      ),
  });

  const {title, description, address, ville, postCode, minBudget, maxBudget} =
    offer;

  return (
    <div className="row flex justify-center items-center">
      <div className="col-xl-10">
        <div className="card">
          <div className="card-body">
            <Formik
              initialValues={{
                title,
                description: description ?? "",
                address,
                ville,
                postCode,
                street: address,
                streetNumber: address,
                minBudget,
                maxBudget,
              }}
              validationSchema={formSchema}
              onSubmit={async (values, {setSubmitting}) => {
                setSubmitting(true);
                if (!subCatIds.length) {
                  setSubCatIdsError(true);
                  setSubmitting(false);
                } else {
                  try {
                    const offerValues = {...values};
                    delete offerValues.street;
                    delete offerValues.streetNumber;
                    await onUpdateOfferAsync({
                      id: offer.id,
                      body: {
                        ...offerValues,
                        subCategory: subCatIds.map((el) => el.id),
                        files: images,
                        status: offerStatus.VALIDATION_WAITING,
                      },
                      afterModalClose: async () => {
                        await Promise.all(
                          deleteFiles.map((id) => onDelete({id})),
                        );
                        navigate(
                          "/annonce/" +
                            generatePrettyUrl(offer.id, values.title),
                        );
                      },
                      onUploadProgress: ({loaded, total}) =>
                        setUploadProgress(
                          total ? Math.round((loaded / total) * 100) : 0,
                        ),
                    });
                  } finally {
                    setSubmitting(false);
                  }
                }
              }}
            >
              {({
                values,
                errors,
                handleChange,
                setFieldValue,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form
                  onSubmit={handleSubmit}
                  className="mx-auto !px-2 sm:!px-3 md:!px-5 lg:!px-10 xl:!px-14 "
                >
                  <h3 className="text-left font-semibold text-xl ">
                    Modification de l'annonce
                    {/* ({selectedCat?.Sector?.title}) */}
                  </h3>
                  <div className="w-full p-2 flex flex-col justify-center mt-4">
                    <div className="w-full grid grid-cols-2 gap-4">
                      <InputField
                        className={"col-span-2 "}
                        label="Titre de l'annonce"
                        name="title"
                        required
                        value={values.title}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.title}
                      />
                      <FormTextArea
                        className={"col-span-2"}
                        label={"Description"}
                        name={"description"}
                        required
                        value={values.description}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.description}
                      />
                      <CategorySelect
                        categories={categories}
                        selectedCat={selectedCat}
                        handleSelect={handleSelect}
                        handleSelectSub={handleSelectSub}
                        subCatValue={subCatValue}
                        subCatIds={subCatIds}
                        setSubCatIds={setSubCatIds}
                        subCatIdsError={subCatIdsError}
                      />

                      <div className="form-group w-full col-span-2">
                        <label className="required-field-label text-base font-medium leading-3 text-[#7E7E7E]">
                          Adresse
                        </label>
                        <GooglePlacesAutocomplete
                          apiKey={googleMapsApiKey}
                          autocompletionRequest={{
                            componentRestrictions: {country: ["fr"]},
                            types: ["address"],
                          }}
                          selectProps={{
                            defaultInputValue: values.address,
                            value: selectedAddress,
                            onChange: (address) =>
                              handlePlaceSelect(address, setFieldValue),
                            placeholder: "Recherchez une adresse...",
                            styles: {
                              dropdownIndicator: (base) => ({
                                ...base,
                                display: "none", // Hides the dropdown icon
                              }),
                              indicatorSeparator: (base) => ({
                                ...base,
                                display: "none", // Hides the separator line
                              }),
                              control: (base) => ({
                                ...base,
                                borderColor: "#d1d5db", // Matches the other inputs' border color
                                borderWidth: "1px",
                                borderRadius: "0.375rem", // Rounded corners like your other inputs
                                padding: "0.375rem 0.75rem", // Add padding to match the input
                                boxShadow: "none",
                                "&:hover": {
                                  borderColor: "#6b7280", // Hover border color
                                },
                              }),
                              input: (base) => ({
                                ...base,
                                fontSize: "0.875rem", // Matches your other inputs' font size
                                color: "#374151", // Text color
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: "#9ca3af", // Placeholder color
                                fontSize: "0.875rem",
                              }),
                            },
                          }}
                        />
                        {(errors.address ||
                          errors.postCode ||
                          errors.ville ||
                          errors.street ||
                          errors.streetNumber) && (
                          <div
                            className="invalid-feedback animated fadeInUp"
                            style={{display: "block"}}
                          >
                            {errors.address ||
                              errors.postCode ||
                              errors.ville ||
                              errors.street ||
                              errors.streetNumber}
                          </div>
                        )}
                      </div>
                      <InputField
                        className={"col-span-2  md:col-span-1"}
                        label="Budget minimum"
                        placeholder={"minimum(€)"}
                        name="minBudget"
                        type="number"
                        min={1}
                        required
                        value={values.minBudget}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.minBudget}
                      />
                      <InputField
                        className={"col-span-2  md:col-span-1"}
                        label="Budget maximum"
                        placeholder={"maximum(€)"}
                        name="maxBudget"
                        type="number"
                        min={1}
                        required
                        value={values.maxBudget}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.maxBudget}
                      />
                    </div>

                    <FileUpload
                      inputRef={inputRef}
                      handleDiplome={handleDiplome}
                      images={images}
                      handleDelete={handleDelete}
                      files={files}
                      handleDeleteFile={handleDeleteFile}
                      handleDragEnter={handleDragEnter}
                      handleDragOver={handleDragOver}
                      handleDragLeave={handleDragLeave}
                      handleDrop={handleDrop}
                      uploadProgress={uploadProgress}
                      uploading={isSubmitting}
                    />
                    <div className="text-center mt-4 gap-2 w-full sm:flex-row flex-col flex justify-end">
                      <Link
                        to={
                          "/annonce/" +
                          generatePrettyUrl(offer?.id, offer?.title)
                        }
                        className="btn !min-w-[250px] !rounded-lg bg-white !text-[#0D44B1] !border-[#0D44B1] !border border-solid"
                      >
                        Retour
                      </Link>
                      <button
                        data-testid="offer-update-submit"
                        className="btn !min-w-[250px] !rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white sw-btn-next"
                        disabled={isSubmitting}
                        type="submit"
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

const InputField = ({
  label,
  name,
  placeholder,
  type = "text",
  value,
  handleChange,
  handleBlur,
  error,
  className,
  min,
  required = false,
}) => (
  <div className={`form-group flex-grow  ${className}`}>
    <label className="text-base font-medium leading-[18px] text-[#7E7E7E]">
      {label}
    </label>
    <input
      type={type}
      className="form-control"
      placeholder={placeholder}
      name={name}
      min={min}
      aria-required={required}
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
    />
    {error && (
      <div
        className="invalid-feedback animated fadeInUp"
        style={{display: "block"}}
      >
        {error}
      </div>
    )}
  </div>
);
const FormTextArea = ({
  label,
  name,
  value,
  handleChange,
  handleBlur,
  className,
  error,
  touched,
  required = false,
}) => (
  <div className={"form-group  flex flex-col pt-1 " + className}>
    <label className="text-base font-medium leading-3 text-[#7E7E7E]">
      {label}
    </label>
    <textarea
      className="form-control flex-grow"
      name={name}
      aria-required={required}
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
      rows={5}
    />

    {error && touched && (
      <div
        className="invalid-feedback animated fadeInUp"
        style={{display: "block"}}
      >
        {error}
      </div>
    )}
  </div>
);

const CategorySelect = ({
  categories,
  selectedCat,
  handleSelect,
  handleSelectSub,
  subCatValue,
  subCatIds,
  setSubCatIds,
  subCatIdsError,
}) => (
  <div className="flex flex-col w-full gap-2 col-span-2">
    <div className="form-group w-full col-span-2 md:col-span-1 ">
      <label className="required-field-label text-base font-medium leading-3 text-[#7E7E7E]">
        Catégorie
      </label>

      <CustomSelectDropdown
        sectorCategories={categories}
        selectedCategory={selectedCat}
        setSelectedCategory={handleSelect}
      />
    </div>
    <div className="form-group w-full col-span-2 md:col-span-1">
      <label className="required-field-label text-base font-medium leading-3 text-[#7E7E7E]">
        Sous-catégorie
      </label>
      <CustomSelectDropdown
        sectorCategories={selectedCat?.SubCategories}
        selectedCategory={subCatValue}
        setSelectedCategory={handleSelectSub}
        disabled={!selectedCat}
      />

      {subCatIdsError && (
        <div
          className="invalid-feedback animated fadeInUp"
          style={{display: "block"}}
        >
          Veuillez sélectionner au moins une sous-catégorie
        </div>
      )}
      <div className="flex justify-start flex-wrap items-center gap-2 mt-1">
        {subCatIds?.map((ele) => (
          <div
            key={ele.id}
            className="bg-gradient-to-r from-[#0022AA1A] to-[#1A64B91A] gap-2 font-bold text-sm rounded-3xl px-4 py-2 flex justify-center items-center text-[#1A64B9]"
          >
            {ele.title}
            <i
              className="fi fi-rr-cross-small flex justify-center items-center cursor-pointer"
              onClick={() =>
                setSubCatIds((prev) => prev.filter((el) => el.id !== ele.id))
              }
            ></i>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FileUpload = ({
  inputRef,
  handleDiplome,
  images,
  handleDelete,
  files,
  handleDeleteFile,
  handleDragEnter,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  uploadProgress,
  uploading,
}) => (
  <div className="flex flex-col w-full justify-start py-4 gap-3">
    <div className="text-base font-medium leading-3 text-[#7E7E7E]">
      Pièces jointes
    </div>
    <div className=" grid grid-cols-2 w-full gap-4">
      <div className="col-span-2 lg:col-span-1">
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current.click()}
          className="flex flex-col px-3 py-3 justify-center items-center w-full  border-2 border-[#1A64B9] border-dashed rounded-xl cursor-pointer"
        >
          <img src={UpImage} alt="upload" className="w-auto h-[50px] mb-2" />
          <h3 className="font-semibold text-center w-[300px] text-sm text-[#19295C]">
            Faites glisser ou importez vos photos/vidéos
          </h3>
          <div className="font-semibold text-center w-full text-[12px] text-[#7e7e7e]">
            JPG, PNG, MP4 · {MAX_OFFER_ATTACHMENTS} fichiers à la fois · 10 Mo max par fichier
          </div>
        </div>
        <input
          type="file"
          ref={inputRef}
          className="hidden"
          onChange={handleDiplome}
          accept="image/*,video/*"
          multiple
        />
      </div>
      <div className=" grid grid-cols-2 sm:grid-cols-3  w-full col-span-2 lg:col-span-1 gap-4">
        {!!(images.length || files.length) && (
          <div className="col-span-full text-xs font-medium text-[#1A64B9]">
            {images.length + files.length} pièce
            {images.length + files.length > 1 ? "s" : ""} jointe
            {images.length + files.length > 1 ? "s" : ""} affichée
            {images.length + files.length > 1 ? "s" : ""}
          </div>
        )}
        {images.map((image, index) => {
          return (
            <div
              key={`${image.file?.name}-${image.file?.lastModified}-${index}`}
            >
              <UploadPreviewState
                uploading={uploading}
                progress={uploadProgress}
                className="aspect-square"
              >
                {image.file?.type.includes("video") ? (
                  <VideoPreview
                    url={image.imageUrl}
                    handleDelete={() => handleDelete(index)}
                  />
                ) : (
                  <ImagePreview
                    imageUrl={image.imageUrl}
                    handleDelete={() => handleDelete(index)}
                  />
                )}
              </UploadPreviewState>
              <div className="mt-1 truncate text-xs text-[#7e7e7e]">
                {image.file?.name}
              </div>
            </div>
          );
        })}
        {files.map((file) => {
          return (
            <div key={file.id}>
              {file.format === fileFormat.VIDEO ? (
                <VideoPreview
                  url={buildImageUrl(file.path)}
                  handleDelete={() => handleDeleteFile(file.id)}
                />
              ) : (
                <ImagePreview
                  imageUrl={buildImageUrl(file.path)}
                  handleDelete={() => handleDeleteFile(file.id)}
                />
              )}
              <div className="mt-1 truncate text-xs text-[#7e7e7e]">
                {file.name || file.path}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const ImagePreview = ({imageUrl, handleDelete}) => (
  <div className="w-full  aspect-square bg-white flex flex-col justify-center items-center relative rounded-xl">
    <div
      onClick={handleDelete}
      className="w-6 h-6 rounded-full flex justify-center items-center bg-[#FF0000] absolute z-10 top-[10px] right-[10px] text-[16px] cursor-pointer text-white"
    >
      <i className="fi fi-rr-trash flex justify-center items-center"></i>
    </div>
    <ImageComponent
      src={imageUrl}
      alt="Selected"
      className="w-full aspect-square object-cover border-2 border-solid  rounded-xl "
      fallback={
        <img
          src={NoImage}
          alt="Selected"
          className="w-full aspect-square object-cover border-2 border-solid  rounded-xl "
        />
      }
    />
  </div>
);

const VideoPreview = ({url, handleDelete}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const handlePlayVideo = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <div className="w-full  aspect-square bg-white flex flex-col justify-center items-center relative rounded-xl">
      <div
        onClick={handleDelete}
        className="w-6 h-6 rounded-full flex justify-center items-center bg-[#FF0000] absolute z-10 top-[10px] right-[10px] text-[16px] cursor-pointer text-white"
      >
        <i className="fi fi-rr-trash flex justify-center items-center"></i>
      </div>
      {!isPlaying ? (
        <img
          className="w-full aspect-square cursor-pointer object-cover border-2 border-solid rounded-xl"
          src={videoImage}
          alt="thumb"
          onClick={handlePlayVideo}
        />
      ) : (
        <video
          src={url}
          alt="Selected"
          className="w-full aspect-square object-cover border-2 border-solid  rounded-xl "
          type="video/mp4"
          ref={videoRef}
          autoPlay
          controls
        />
      )}
    </div>
  );
};

export default PageUpdateAnnonce;
